import type {
  ApiCollectedProfileInfoRes,
  PublicOpenSearchResponsePagedData,
  SportResponseRecord,
} from '@on3/api';
import { baseSports } from '@on3/ui-lib/store/mocks/sports';
import {
  Collection,
  Results,
} from 'components/GetRecruited/SearchPage/Form/Partials';
import { createContext, useContext, useMemo, useState } from 'react';

import styles from './Form.module.scss';

interface FormContextProps {
  step: number;
  setStep: (step: number) => void;
  info: ApiCollectedProfileInfoRes | null;
  setInfo: (info: ApiCollectedProfileInfoRes) => void;
  results: PublicOpenSearchResponsePagedData | null;
  setResults: (results: PublicOpenSearchResponsePagedData) => void;
}

const FormContext = createContext<FormContextProps>({
  step: 0,
  setStep: (step: number) => {},
  info: null,
  setInfo: (info: ApiCollectedProfileInfoRes) => {},
  results: null,
  setResults: (results: PublicOpenSearchResponsePagedData) => {},
});

export const useGetRecruitedForm = () => {
  const context = useContext(FormContext);

  if (!context) {
    throw new Error(
      'useGetRecruitedForm must be used within a GetRecruitedForm',
    );
  }

  return context;
};

export type SearchPageFormProps = {
  profileData?: ApiCollectedProfileInfoRes | null;
  profileResults?: PublicOpenSearchResponsePagedData | null;
  sports?: SportResponseRecord[];
};

export const SearchPageForm = ({
  profileData,
  profileResults,
  sports,
}: SearchPageFormProps) => {
  const [step, setStep] = useState<number>(profileResults ? 1 : 0);
  const [info, setInfo] = useState<ApiCollectedProfileInfoRes | null>(
    profileData ?? null,
  );
  const [results, setResults] =
    useState<PublicOpenSearchResponsePagedData | null>(profileResults ?? null);

  const collectionSports = sports || baseSports;

  const hasNoMatches = step === 1 && results?.list?.length === 0;
  const hasMatch = step === 1 && results?.list?.length === 1;
  const hasMultipleMatches = step === 1 && (results?.list ?? [])?.length > 1;

  const headings = useMemo(() => {
    if (hasNoMatches) {
      return {
        heading: 'Complete profile setup.',
        subheading: 'Create an On3 account.',
      };
    }

    if (hasMatch) {
      return {
        heading: 'Nice work, you have a profile at On3 ready to be claimed.',
        subheading:
          'We found a profile that matches your information. If this is you, please proceed below.',
      };
    }

    if (hasMultipleMatches) {
      return {
        heading: 'Nice work! Your profile already exists on On3.',
        subheading:
          'We found profiles that match your information. If one of these are you, please proceed below.',
      };
    }

    return {
      heading: "Let's start with some athlete info.",
      subheading: 'Parents or Athletes enter player information here.',
    };
  }, [hasMatch, hasMultipleMatches, hasNoMatches]);

  return (
    <FormContext.Provider
      value={{ step, setStep, info, setInfo, results, setResults }}
    >
      <div className={styles.block}>
        <header className={styles.header}>
          <h1>{headings.heading}</h1>
          <p>{headings.subheading}</p>
        </header>
        <section>
          {step === 0 && <Collection sports={collectionSports} />}
          {step === 1 && <Results />}
        </section>
      </div>
    </FormContext.Provider>
  );
};
